/**
 * Default lesson layout — the room every lesson without its own layout uses.
 *
 * Stored in app_settings/lesson_layout, which every signed-in viewer reads and
 * only superadmin and associate may write. Existing lessons pick it up the next
 * time they open; nothing is migrated.
 */

import { useEffect, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LayoutTemplate, Loader2 } from 'lucide-react';

import { Button } from '../../Components/ui/button';
import { LayoutEditor, type LayoutValue } from '../../Components/studio/layout/LayoutEditor';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { resolveLessonLayout } from '../../lib/three/lessonLayout';

/** The fields a default can set: nothing that names a particular lesson's models. */
function defaultFields(value: LayoutValue): LayoutValue {
  const r = resolveLessonLayout(value);
  return {
    preset: r.preset,
    panelSide: r.panelSide,
    modelScale: r.modelScale,
    deskReach: r.deskReach,
    realScale: r.realScale,
    roomTheme: r.roomTheme,
    showProps: r.showProps,
  };
}

export default function LessonLayoutDefaults() {
  const { user } = useAuth();
  const [value, setValue] = useState<LayoutValue>({});
  const [saved, setSaved] = useState<LayoutValue>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'app_settings', 'lesson_layout'))
      .then((snap) => {
        const stored = snap.exists() ? defaultFields(snap.data() as LayoutValue) : {};
        setValue(stored);
        setSaved(stored);
      })
      .catch((err) => {
        console.warn('Could not read the default lesson layout:', err);
        toast.error('Could not load the default lesson layout');
      })
      .finally(() => setLoading(false));
  }, []);

  const dirty = JSON.stringify(defaultFields(value)) !== JSON.stringify(defaultFields(saved));

  const save = async () => {
    setSaving(true);
    try {
      const next = defaultFields(value);
      await setDoc(doc(db, 'app_settings', 'lesson_layout'), {
        ...next,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid ?? null,
      });
      setSaved(next);
      toast.success('Default lesson layout saved. Lessons without their own layout use it from their next launch.');
    } catch (err) {
      console.error('Saving the default lesson layout failed:', err);
      toast.error('Could not save the default lesson layout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
              <LayoutTemplate className="w-5 h-5 text-primary" />
              Default lesson layout
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              How the room is arranged in the lesson player for every lesson that has no layout of its own: the desk, the 3D
              models on it and the lesson panel. A lesson's own Layout tab overrides this.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" disabled={!dirty || saving} onClick={() => setValue(saved)}>
              Discard
            </Button>
            <Button disabled={!dirty || saving} onClick={save}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save default
            </Button>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <LayoutEditor value={value} onChange={setValue} assets={[]} readOnly={saving} />
        )}
      </div>
    </div>
  );
}
